import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import { UserContext } from '../hooks';
import axiosInstance from '../services/axiosInstance';
import {
  getRequiredUserDataErrors,
  isValidDni,
  isValidEmail,
  isValidPhone,
  isValidSurname,
  MAX_DNI_PHONE_LENGTH,
  MAX_SURNAME_LENGTH,
  normalizeDni,
  normalizePhone,
} from '../utils/validation';

const CompleteProfileDialog = ({ open, onClose, onSaved }) => {
  const { user, updateUser } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    dni: '',
    phone: '',
    role: '',
  });

  useEffect(() => {
    if (open && user) {
      setFormData({
        name: user.name || '',
        surname: user.surname || '',
        email: user.email || '',
        dni: user.dni || '',
        phone: user.phone || '',
        role: user.role || '',
      });
    }
  }, [open, user]);

  const missingFields = getRequiredUserDataErrors(formData);

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Introduce tu nombre.');
      return;
    }

    if (!isValidSurname(formData.surname)) {
      toast.error(
        'Los apellidos son obligatorios y deben tener máximo 40 caracteres.'
      );
      return;
    }

    if (!isValidEmail(formData.email)) {
      toast.error('Introduce un email válido.');
      return;
    }

    if (!isValidDni(formData.dni)) {
      toast.error(
        'El DNI/NIE es obligatorio y debe tener máximo 20 caracteres.'
      );
      return;
    }

    if (!isValidPhone(formData.phone)) {
      toast.error(
        'El teléfono es obligatorio y debe tener máximo 20 caracteres.'
      );
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      payload.append('name', formData.name.trim());
      payload.append('surname', formData.surname.trim());
      payload.append('email', formData.email.trim().toLowerCase());
      payload.append('dni', normalizeDni(formData.dni));
      payload.append('phone', normalizePhone(formData.phone));

      const response = await axiosInstance.put('/users/update', payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (!response.success) {
        toast.error(response.message || 'No se pudieron guardar los datos.');
        return;
      }

      updateUser({
        name: response.user.name,
        surname: response.user.surname,
        email: response.user.email,
        dni: response.user.dni,
        phone: response.user.phone,
        dob: response.user.dob?.split('T')[0],
        role: response.user.role,
        image: response.user.image,
        universityCard: response.user.universityCard,
        subscriptions: response.user.subscriptions,
      });

      toast.success('Datos guardados correctamente.');
      onClose();
      onSaved?.(response.user);
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Error al guardar los datos.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Completa tus datos</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Para continuar debes completar tus datos básicos. Después podrás
          seguir con la reserva o el alta.
        </Typography>
        {missingFields.length > 0 && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            Faltan o no son válidos: {missingFields.join(', ')}.
          </Typography>
        )}
        <TextField
          fullWidth
          required
          label="Nombre"
          name="name"
          value={formData.name}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          required
          label="Apellidos"
          name="surname"
          value={formData.surname}
          inputProps={{ maxLength: MAX_SURNAME_LENGTH }}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          required
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          required
          label="DNI/NIE"
          name="dni"
          value={formData.dni}
          inputProps={{ maxLength: MAX_DNI_PHONE_LENGTH }}
          onChange={event =>
            setFormData(prev => ({
              ...prev,
              dni: event.target.value.toUpperCase(),
            }))
          }
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          required
          label="Teléfono"
          name="phone"
          type="tel"
          value={formData.phone}
          inputProps={{ maxLength: MAX_DNI_PHONE_LENGTH }}
          onChange={handleChange}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          variant="contained"
          size="small"
        >
          {loading ? <CircularProgress size={24} /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteProfileDialog;
